import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  useApplyPtoRdCarryOverMutation,
  useGetPtoRdCarryOverQuery,
  useListPtoRdDocumentsQuery,
  useListPtoRevisionsQuery,
  type PtoRdCarryOverItem,
  type PtoRdDocument,
} from '@shared/api';
import {
  carryOverAppliedText,
  carryOverMovable,
  carryOverPairs,
  carryOverQtyText,
  carryOverStatusView,
  carryOverTotals,
} from '@entities/field';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { ChangeDocCarryOver } from './rdEdits/ChangeDocCarryOver';

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

/** Свёрнутый блок: проверка по кнопке, итог «переносить нечего». */
const CompactPanel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.h3`
  margin: 0;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13.5px;
  line-height: 1.55;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 52vh;
  margin-top: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12.5px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
    white-space: nowrap;
  }
  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  tr.group td {
    background: ${({ theme }) => theme.colors.surface};
    font-weight: 600;
  }
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const OkNote = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Источник позиции коротко: текст РД или таблица спецификации. */
function sourceShort(role: string): string {
  return role === 'rd' ? 'текст РД' : 'таблица';
}

/** Позиции одного документа: ревизии «было → стало» подписываем номерами. */
function DocumentRows({
  document,
  documentId,
  items,
  excluded,
  selectable,
  onToggle,
}: {
  document: PtoRdDocument | undefined;
  documentId: string;
  items: PtoRdCarryOverItem[];
  excluded: ReadonlySet<string>;
  selectable: boolean;
  onToggle: (oldPositionId: string, checked: boolean) => void;
}) {
  // Тот же запрос, что у панели ревизий документа, — из кэша.
  const { data } = useListPtoRevisionsQuery({ documentId, deleted: false });
  const revNo = useMemo(
    () => new Map((data?.items ?? []).map((r) => [r.id, r.revision_no])),
    [data],
  );
  const rev = (id: string | null) => {
    const n = id ? revNo.get(id) : undefined;
    return n === undefined ? '?' : String(n);
  };
  return (
    <>
      <tr className="group">
        <td colSpan={selectable ? 8 : 7}>
          {document ? `${document.code} — ${document.title}` : 'Документ РД'}
        </td>
      </tr>
      {items.map((i) => {
        const st = carryOverStatusView(i.status);
        const movable = carryOverMovable(i);
        return (
          <tr key={i.old_position_id}>
            {selectable && (
              <td>
                {movable && (
                  <input
                    type="checkbox"
                    aria-label={`Перенести привязки позиции «${i.name}»`}
                    checked={!excluded.has(i.old_position_id)}
                    onChange={(e) => onToggle(i.old_position_id, e.target.checked)}
                  />
                )}
              </td>
            )}
            <td>
              {i.name}
              {i.new_name && i.new_name !== i.name && <Sub>в новой ревизии: {i.new_name}</Sub>}
              <Sub>{sourceShort(i.source_role)}</Sub>
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {movable
                ? `${rev(i.old_revision_id)} → ${rev(i.new_revision_id)}`
                : rev(i.old_revision_id)}
            </td>
            <td className="num">{carryOverQtyText(i)}</td>
            <td>
              <Badge $tone={st.tone} title={st.hint}>
                {st.label}
              </Badge>
            </td>
            <td className="num">{i.elements}</td>
            <td className="num">
              {i.facts}
              {i.facts_accepted > 0 && <Sub>принято {i.facts_accepted}</Sub>}
            </td>
            <td className="num">{i.journal}</td>
          </tr>
        );
      })}
    </>
  );
}

/**
 * Блок «Новая ревизия РД» в карточке проекта ПТО: позиции по документам
 * изменения РД (№8, Б), ждущие решения при новой ревизии (появилось / нет в
 * ревизии / спорно — `rdEdits/ChangeDocCarryOver`), и перенос привязок Прораба
 * на действующую ревизию.
 */
export function RdCarryOverPanel(props: {
  projectId: string;
  /** Право записи в ПТО (сервер дополнительно проверяет команду проекта). */
  canWrite: boolean;
  /** Проект в корзине — переносить нельзя (сервер: 409). */
  projectDeleted: boolean;
}) {
  return (
    <>
      {!props.projectDeleted && <ChangeDocCarryOver projectId={props.projectId} />}
      <ProrabCarryOverPanel {...props} />
    </>
  );
}

/**
 * Перенос привязок Прораба на новую ревизию РД: позиции заменённых ревизий,
 * на которых остались привязки Прораба (элементы конструктива, факты объёма,
 * строки ОЖР), и их продолжения в действующей ревизии того же документа —
 * предпросмотр `GET /projects/{id}/rd-carry-over`. Пока привязки не перенесены,
 * сверка фактов идёт по старому количеству, а накопительный контроль новой
 * позиции пуст. Перенос — явное действие инженера с правом записи: уходят
 * отмеченные однозначные пары (`POST` с `pairs`); позиции без однозначного
 * продолжения переносят вручную на странице площадки.
 *
 * Предпросмотр — по кнопке «Показать», а не при каждом открытии проекта (A13):
 * он просматривает журнал проекта по каждой позиции заменённых ревизий, и на
 * объекте с десятками ревизий страница проекта платила за это каждый раз (и
 * после любого действия в Прорабе — план зависит от его привязок).
 */
function ProrabCarryOverPanel({
  projectId,
  canWrite,
  projectDeleted,
}: {
  projectId: string;
  /** Право записи в ПТО (сервер дополнительно проверяет команду проекта). */
  canWrite: boolean;
  /** Проект в корзине — переносить нельзя (сервер: 409). */
  projectDeleted: boolean;
}) {
  // Проверку запускает инженер; до этого запрос не уходит.
  const [requested, setRequested] = useState(false);
  const q = useGetPtoRdCarryOverQuery(projectId, { skip: !projectId || !requested });
  // Шифры документов — тот же запрос, что у списка документов проекта (из кэша).
  const { data: docsData } = useListPtoRdDocumentsQuery(
    { projectId, deleted: false },
    { skip: !projectId || !q.data?.items.length },
  );
  const [apply, { isLoading: applying }] = useApplyPtoRdCarryOverMutation();
  // Снятые инженером позиции (по умолчанию отмечены все однозначные).
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set());
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const summary = q.data?.summary;
  const groups = useMemo(() => {
    const by = new Map<string, PtoRdCarryOverItem[]>();
    for (const i of items) {
      const list = by.get(i.document_id);
      if (list) list.push(i);
      else by.set(i.document_id, [i]);
    }
    return [...by.entries()];
  }, [items]);
  const docById = useMemo(() => new Map((docsData?.items ?? []).map((d) => [d.id, d])), [docsData]);
  const totals = carryOverTotals(items, excluded);
  const manual = items.filter((i) => !carryOverMovable(i)).length;
  const selectable = canWrite && !projectDeleted;

  function toggle(oldPositionId: string, checked: boolean) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(oldPositionId);
      else next.add(oldPositionId);
      return next;
    });
  }

  async function onApply() {
    const pairs = carryOverPairs(items, excluded);
    if (pairs.length === 0) return;
    if (
      !window.confirm(
        `Перенести привязки ${pairs.length} поз. на действующую ревизию РД? Элементы ` +
          'конструктива, факты объёма (вместе с принятыми) и строки ОЖР перейдут на новые ' +
          'позиции; сверка непринятых фактов пересчитается по новому количеству.',
      )
    ) {
      return;
    }
    setNote(null);
    try {
      const res = await apply({ projectId, pairs }).unwrap();
      setNote({ ok: true, text: carryOverAppliedText(res.applied) });
      setExcluded(new Set());
    } catch (e) {
      setNote({ ok: false, text: apiErrorText(e, 'Не удалось перенести привязки.') });
    }
  }

  if (!requested) {
    return (
      <CompactPanel>
        <Head style={{ marginBottom: 0 }}>
          <Title>Новая ревизия РД</Title>
          <Button $variant="secondary" $size="sm" onClick={() => setRequested(true)}>
            Показать
          </Button>
        </Head>
        <Muted>
          Остались ли привязки Прораба (элементы конструктива, факты объёма, строки ОЖР) на позициях
          заменённых ревизий РД и куда их перенести. Проверка просматривает журнал проекта —
          запускайте её после загрузки новой ревизии.
        </Muted>
      </CompactPanel>
    );
  }

  const hide = (
    <Button
      $variant="ghost"
      $size="sm"
      onClick={() => {
        setRequested(false);
        setNote(null);
      }}
    >
      Скрыть
    </Button>
  );

  if (q.isLoading || (q.isFetching && !q.data)) {
    return (
      <CompactPanel>
        <Head style={{ marginBottom: 0 }}>
          <Title>Новая ревизия РД</Title>
          {hide}
        </Head>
        <Muted>Проверяю привязки Прораба на заменённых ревизиях РД…</Muted>
      </CompactPanel>
    );
  }
  // Старый сервер без переноса (404) и нет доступа (403) — объясняем, а не
  // молчим: проверку запросили кнопкой.
  const status = apiErrorStatus(q.error);
  if (items.length === 0) {
    return (
      <CompactPanel>
        <Head style={{ marginBottom: 0 }}>
          <Title>Новая ревизия РД</Title>
          {hide}
        </Head>
        {/* Всё перенесено — итог остаётся виден, пока блок открыт. */}
        {note ? (
          note.ok ? (
            <OkNote>{note.text}</OkNote>
          ) : (
            <ErrorNote>{note.text}</ErrorNote>
          )
        ) : q.isError ? (
          <ErrorNote>
            {status === 403
              ? 'Нет доступа к переносу привязок: его ведёт команда проекта в ОСНОВА.Инженер.'
              : apiErrorText(q.error, 'Не удалось проверить привязки к новой ревизии РД.')}
          </ErrorNote>
        ) : (
          <Muted>Привязок Прораба на позициях заменённых ревизий РД нет — переносить нечего.</Muted>
        )}
      </CompactPanel>
    );
  }

  return (
    <Panel>
      <Head>
        <Title>Новая ревизия РД: привязки Прораба на заменённых позициях</Title>
        <Summary style={{ marginTop: 0 }}>
          <Badge $tone="warning">позиций: {items.length}</Badge>
          {hide}
        </Summary>
      </Head>
      <Muted>
        РД заменена новой ревизией, а элементы конструктива, факты объёма и строки ОЖР остались на
        позициях прежней. Пока они не перенесены, сверка фактов идёт по старому количеству, а
        накопительный контроль новой позиции начинается с нуля.
      </Muted>
      {summary && (
        <Summary>
          <Badge $tone="info">однозначно: {summary.movable}</Badge>
          {summary.quantity_changed > 0 && (
            <Badge $tone="warning">количество изменилось: {summary.quantity_changed}</Badge>
          )}
          {summary.paraphrase > 0 && (
            <Badge $tone="info">пересказ наименования: {summary.paraphrase}</Badge>
          )}
          {summary.not_found > 0 && (
            <Badge $tone="danger">нет продолжения: {summary.not_found}</Badge>
          )}
          {summary.ambiguous > 0 && <Badge $tone="danger">неоднозначно: {summary.ambiguous}</Badge>}
        </Summary>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              {selectable && <th aria-label="Переносить" />}
              <th>Позиция</th>
              <th>Ревизия</th>
              <th>Количество</th>
              <th>Продолжение</th>
              <th>Элементы</th>
              <th>Факты</th>
              <th>ОЖР</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([documentId, list]) => (
              <DocumentRows
                key={documentId}
                documentId={documentId}
                document={docById.get(documentId)}
                items={list}
                excluded={excluded}
                selectable={selectable}
                onToggle={toggle}
              />
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {manual > 0 && (
        <Muted style={{ marginTop: 8 }}>
          Без однозначного продолжения — {manual} поз.: их привязки автоматически не переносятся.
          Элементу назначьте позицию на странице площадки («Конструктив» → «Сменить позицию РД…»):
          факты элемента без позиции получат её же.
        </Muted>
      )}

      {selectable ? (
        <Row>
          <Button
            $variant="primary"
            disabled={applying || totals.positions === 0}
            onClick={() => void onApply()}
          >
            {applying
              ? 'Переношу…'
              : `Перенести привязки на действующую ревизию (${totals.positions})`}
          </Button>
          {totals.positions > 0 && (
            <Muted>
              Уйдёт: элементов {totals.elements}, фактов {totals.facts}
              {totals.factsAccepted > 0 ? ` (принятых ${totals.factsAccepted})` : ''}, строк ОЖР{' '}
              {totals.journal}.
            </Muted>
          )}
        </Row>
      ) : (
        <Muted style={{ marginTop: 8 }}>
          {projectDeleted
            ? 'Проект в корзине — привязки не переносятся, пока проект не восстановлен.'
            : 'Перенести привязки может участник команды проекта с правом записи в ОСНОВА.Инженер.'}
        </Muted>
      )}
      {note && (note.ok ? <OkNote>{note.text}</OkNote> : <ErrorNote>{note.text}</ErrorNote>)}
    </Panel>
  );
}
